import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import Diagnostics from "./Diagnostics";
import Records from "./Records";
import Treatments from "./Treatments";
import VitalSigns from "./VitalSigns";
import Labs from "./Labs";
import "./print.css";
import logo from "./drapplogo.png";

let html2pdfLoadPromise = null;

const loadHtml2Pdf = () => {
  if (window.html2pdf) return Promise.resolve();
  if (html2pdfLoadPromise) return html2pdfLoadPromise;
  const existing = document.querySelector('script[src*="html2pdf.bundle.js"]');
  if (existing) {
    html2pdfLoadPromise = new Promise((resolve, reject) => {
      const start = Date.now();
      const maxWait = 15000;
      const check = () => {
        if (window.html2pdf) return resolve();
        if (Date.now() - start > maxWait) {
          html2pdfLoadPromise = null;
          return reject(new Error("Timeout loading html2pdf"));
        }
        setTimeout(check, 100);
      };
      check();
    });
    return html2pdfLoadPromise;
  }
  html2pdfLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "") || "";
    script.src = `${base}/html2pdf.bundle.js`;
    script.async = true;
    script.onload = () => resolve(window.html2pdf);
    script.onerror = () => {
      script.remove();
      html2pdfLoadPromise = null;
      reject(new Error("Failed to load html2pdf"));
    };
    document.body.appendChild(script);
  });
  return html2pdfLoadPromise;
};

function PrintModal(props) {
  const { t } = useTranslation();

  function _calculateAge(birthday) {
    var ageDifMs = Date.now() - birthday.getTime();
    var ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  const [labMode, setLabMode] = useState("last");
  const [vitalsMode, setVitalsMode] = useState("last");
  const [showBtns, setShowBtns] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const safeSetState = (setter, value) => {
    if (mountedRef.current) setter(value);
  };

  const licenses =
    props.resource &&
    props.resource.licenses &&
    props.resource.licenses
      .map((license) => {
        if (license.jurisdiction === "AR") {
          return `M.N. ${license.value}`;
        } else {
          return `M.P. ${license.value}`;
        }
      })
      .join("-");

  const recordsFiltered = (props.records || [])
    .filter((element) => !element.deleted)
    .map((element) => ({ ...element, display: true }));

  const diagnosticsChronicFiltered = (props.diagnostics || [])
    .filter((element) => !element.deleted)
    .filter((element) => element.status === "chronic")
    .map((element) => ({ ...element, display: true }));

  const diagnosticsIntercurrenceFiltered = (props.diagnostics || [])
    .filter((element) => !element.deleted)
    .filter((element) => element.status === "intercurrence")
    .map((element) => ({ ...element, display: true }));

  const diagnosticsPendingFiltered = (props.diagnostics || [])
    .filter((element) => !element.deleted)
    .filter((element) => element.status === "pending")
    .map((element) => ({ ...element, display: true }));

  const activeTreatments = (props.treatments || [])
    .filter((element) => !element.deleted)
    .filter((element) => element.status === "active");

  const orderedLabs = (props.labs || [])
    .filter((element) => !element.deleted)
    .sort((a, b) => b.startsAt - a.startsAt);

  const vitalSigns = (props.vitalSigns || [])
    .filter((element) => !element.deleted)
    .sort((a, b) => b.startsAt - a.startsAt);

  const [printStatus, setPrintStatus] = useState({
    diagnosticsChronic: true,
    diagnosticsIntercurrence: true,
    diagnosticsPending: true,
    diagnosticsNotes: true,
    treatmentsActive: true,
    treatmentsNotes: true,
    records: true,
    individualRecords: [],
    individualDiagnosticsChronic: [],
    individualDiagnosticsIntercurrence: [],
    individualDiagnosticsPending: [],
    individualTreatmentsActive: [],
    lastLab: true,
    orderedLabs: [],
    vitalSigns: true,
    individualVitalSigns: [],
  });

  const download = async () => {
    safeSetState(setShowBtns, false);
    safeSetState(setDownloading, true);
    try {
      await loadHtml2Pdf();
      const el = document.getElementById("download");
      if (!el) throw new Error("Elemento para descargar no encontrado");
      const date = new Date();
      const lastName = (props.consumer?.data?.lastName ?? "").replace(/[\\/:*?"<>|]/g, "");
      const firstName = (props.consumer?.data?.firstName ?? "").replace(/[\\/:*?"<>|]/g, "");
      const baseName = `${lastName}${firstName}`.trim() || "documento";
      const filename = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}-${baseName}.pdf`;
      await window
        .html2pdf()
        .from(el)
        .toPdf()
        .save(filename);
    } catch (err) {
      try {
        window.notify?.({ message: err?.message || "Error al descargar PDF", type: "danger" });
      } catch {}
    } finally {
      safeSetState(setDownloading, false);
      setTimeout(() => safeSetState(setShowBtns, true), 500);
    }
  };

  useEffect(() => {
    setPrintStatus({
      ...printStatus,
      individualRecords: recordsFiltered,
      individualDiagnosticsChronic: diagnosticsChronicFiltered,
      individualDiagnosticsIntercurrence: diagnosticsIntercurrenceFiltered,
      individualDiagnosticsPending: diagnosticsPendingFiltered,
      individualTreatmentsActive: activeTreatments,
      orderedLabs: orderedLabs,
      individualVitalSigns: vitalSigns,
    });
  }, [
    props.records,
    props.diagnostics,
    props.treatments,
    props.labs,
    props.vitalSigns,
  ]);

  return (
    <div>
      {/* Bug real (auditoría 2026-08-21): 6 controles (3 botones + "Notas:"
          + 2 toggles con texto largo) sin flex-wrap — en mobile no entraban
          en una línea y no había forma de que colapsen. */}
      <div className="d-flex align-items-center noPrint py-2 flex-wrap">
        <button
          className="btn btn-sm btn-outline-success mx-2"
          onClick={() => {
            window.print();
          }}
        >
          {t("ehr:print")}
        </button>
        <button
          className="btn btn-sm btn-outline-success mx-2"
          onClick={download}
          disabled={downloading}
        >
          {downloading ? t("ehr:loading", "Descargando...") : t("ehr:download")}
        </button>
        <button
          className="btn btn-sm btn-outline-primary mx-2"
          onClick={() => {
            setPrintStatus({
              diagnosticsChronic: true,
              diagnosticsIntercurrence: true,
              diagnosticsPending: true,
              diagnosticsNotes: true,
              treatmentsActive: true,
              treatmentsNotes: true,
              records: true,
              individualRecords: recordsFiltered,
              individualDiagnosticsChronic: diagnosticsChronicFiltered,
              individualDiagnosticsIntercurrence: diagnosticsIntercurrenceFiltered,
              individualDiagnosticsPending: diagnosticsPendingFiltered,
              individualTreatmentsActive: activeTreatments,
              lastLab: true,
              orderedLabs: orderedLabs,
              vitalSigns: true,
              individualVitalSigns: vitalSigns,
            });
          }}
        >
          {t("ehr:show_all")}
        </button>

        <small className="ms-4 noPrint">{t("notes")}:</small>

        <button
          className={
            printStatus.diagnosticsNotes
              ? "btn btn-link btn-sm text-danger"
              : "btn btn-link btn-sm text-success"
          }
          onClick={() => {
            setPrintStatus({
              ...printStatus,
              diagnosticsNotes: !printStatus.diagnosticsNotes,
            });
          }}
        >
          {printStatus.diagnosticsNotes
            ? t("ehr:hide_in_diagnostics")
            : t("ehr:show_in_diagnostics")}
        </button>

        <button
          className={
            printStatus.treatmentsNotes
              ? "btn btn-link btn-sm text-danger"
              : "btn btn-link btn-sm text-success"
          }
          onClick={() => {
            setPrintStatus({
              ...printStatus,
              treatmentsNotes: !printStatus.treatmentsNotes,
            });
          }}
        >
          {printStatus.treatmentsNotes
            ? t("ehr:hide_in_treatments")
            : t("ehr:show_in_treatments")}
        </button>
      </div>

      <div className={props.className || ""}>
        <div className="modal-body print" id="download">
          <div className="d-flex flex-column mb-4">
            <h2 className="h2 fw-bold mx-4 mt-2">
              {t("ehr")}
              <img src={logo} width="130px" className="mx-4 align-middle"></img>
            </h2>

            <div className="d-flex px-4 justify-content-between mt-3">
              <div className="d-flex flex-column">
                <span className="fw-bold">
                  {props.consumer.data.lastName} {props.consumer.data.firstName}
                </span>
                <small className="fst-italic">
                  DNI {props.consumer.data.identification}
                </small>
                <small>
                  {_calculateAge(new Date(props.consumer.data.dob))} años{" "}
                  {t(props.consumer.data.gender)}
                </small>
                {/* Bug real (auditoría 2026-08-16): crash de toda la app — financiers no
                    siempre está presente en consumer.data (ver el mismo guard aplicado a este
                    campo en src/App/EHR/Consumer/Consumer.js), y esta vista de impresión no
                    tiene error boundary local: un TypeError acá tira abajo toda la app
                    autenticada a la pantalla genérica de error. */}
                {(props.consumer.data.financiers || []).map((financier) => {
                  return (
                    <span>
                      {financier.label} {financier.identification}
                    </span>
                  );
                })}
              </div>

              <div className="d-flex flex-column">
                <span className="fw-bold">
                  {props.resource && props.resource.label}
                </span>
                <small className="fst-italic">
                  {licenses && licenses.replace(/-/g, " ")}
                </small>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="">
              {printStatus.diagnosticsChronic &&
                printStatus.individualDiagnosticsChronic &&
                printStatus.individualDiagnosticsChronic.length > 0 && (
                  <Diagnostics
                    type="chronic"
                    showBtns={showBtns}
                    array={printStatus.individualDiagnosticsChronic}
                    printStatus={printStatus}
                    setPrintStatus={setPrintStatus}
                  ></Diagnostics>
                )}

              {printStatus.diagnosticsIntercurrence &&
                printStatus.individualDiagnosticsIntercurrence &&
                printStatus.individualDiagnosticsIntercurrence.length > 0 && (
                  <Diagnostics
                    type="intercurrence"
                    showBtns={showBtns}
                    array={printStatus.individualDiagnosticsIntercurrence}
                    printStatus={printStatus}
                    setPrintStatus={setPrintStatus}
                  ></Diagnostics>
                )}

              {printStatus.diagnosticsPending &&
                printStatus.individualDiagnosticsPending &&
                printStatus.individualDiagnosticsPending.length > 0 && (
                  <Diagnostics
                    type="pending"
                    showBtns={showBtns}
                    array={printStatus.individualDiagnosticsPending}
                    printStatus={printStatus}
                    setPrintStatus={setPrintStatus}
                  ></Diagnostics>
                )}
            </div>
          </div>

          <div className="mt-3">
            <div className="p-1">
              {printStatus.treatmentsActive &&
                activeTreatments &&
                activeTreatments.length > 0 && (
                  <Treatments
                    activeTreatments={activeTreatments}
                    showBtns={showBtns}
                    printStatus={printStatus}
                    setPrintStatus={setPrintStatus}
                  ></Treatments>
                )}
            </div>
          </div>

          <div className="mt-3">
            <div className="p-1">
              {printStatus.records &&
                recordsFiltered &&
                recordsFiltered.length > 0 && (
                  <Records
                    showBtns={showBtns}
                    printStatus={printStatus}
                    setPrintStatus={setPrintStatus}
                  ></Records>
                )}
            </div>
          </div>

          <div className="mt-3">
            <div className="p-1">
              {printStatus.vitalSigns && vitalSigns.length > 0 && (
                <VitalSigns
                  vitalsMode={vitalsMode}
                  setVitalsMode={setVitalsMode}
                  setPrintStatus={setPrintStatus}
                  printStatus={printStatus}
                  showBtns={showBtns}
                  vitalSigns={vitalSigns}
                ></VitalSigns>
              )}
            </div>
          </div>

          <div className="mt-3">
            <div className="p-1">
              {printStatus.lastLab && orderedLabs.length > 0 && (
                <Labs
                  labMode={labMode}
                  setLabMode={setLabMode}
                  setPrintStatus={setPrintStatus}
                  printStatus={printStatus}
                  showBtns={showBtns}
                ></Labs>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrintModal;
